import { formatDate } from '@dansk-metal/utils/date';

import { Period } from '@web/blocks/apprentice-salary-calculator-block/calculation/apprentice-salary-calculation';

type FormAnswers = {
	employmentType: string;
	education: string;
	endDate: string;
	isBasicEducation: string;
	isAdultApprentice: string;
};

export default function generatePdfBody(formData: FormAnswers, result: Period[]) {
	return `
		<html>
			<head>
				<style>
					@import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;600;800&display=swap');
					@font-face {
						font-family: 'Raleway';
						src: url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;600;800&display=swap') format('woff2');
					}

					:root {
						--font-weight-extra-bold: 800;
						--font-weight-semi-bold: 600;
						--color-dark-blue-white90: #e5e9eb;
						--color-light-blue-white75: #f3fafb;
						--color-middle-blue-base: #00aace;
						--color-light-blue-base: #cee9ef;
					}

					body {
						font-family: Raleway;
						margin: 0;
						padding: 0;
					}

					table {
						width: 100%;
						text-align: left;
						border-collapse: collapse;
					}

					thead {
						background-color: var(--color-light-blue-white75);
					}

					thead th {
						font-weight: var(--font-weight-semi-bold);
					}

					a {
						color: var(--color-middle-blue-base);
						text-decoration: underline;
					}

					.print-content {
						margin: 40px;
						max-width: 800px;
						font-size: 12px;
					}

					.print-content h2 {
						margin-bottom: 8px;
						font-size: 16px;
					}

					.print-content__header {
						display: flex;
						flex-direction: row;
						justify-content: space-between;
						margin-bottom: 40px;
					}

					.print-content__header h1 {
						font-weight: var(--font-weight-extra-bold);
						font-size: 28px;
						margin: 0;
					}

					.print-content__header p {
						padding-top: 16px;
						white-space: nowrap;
						margin: 0;
					}

					.print-content__salary__table {
						margin-top: 16px;
						margin-bottom: 8px;
					}

					.print-content__salary__table td,
					.print-content__salary__table th {
						font-size: 12px;
						padding: 12px 24px;
						border-bottom: 1px solid var(--color-dark-blue-white90);
					}

					.print-content__salary__table td:nth-child(-n+2),
					.print-content__salary__table th:nth-child(-n+2) {
						width: 30%;
					}

					.print-content__salary__table td:nth-child(2),
					.print-content__salary__table th:nth-child(2) {
						border-right: 1px solid var(--color-dark-blue-white90);
						border-left: 1px solid var(--color-dark-blue-white90);
					}

					.print-content__salary__value {
						font-size: 14px;
						font-weight: var(--font-weight-semi-bold);
					}

					.print-content__info-text {
						padding-left: 24px;
						text-align: left;
					}

					.print-content__form-answers-table {
						display: flex;
						flex-direction: column;
						gap: 16px;
					}

					.print-content__form-answers-table td,
					.print-content__form-answers-table th {
						font-size: 12px;
						padding: 4px 24px;
						border-bottom: 1px solid var(--color-dark-blue-white90);
					}

					.print-content__form-answers-table td:nth-child(1),
					.print-content__form-answers-table th:nth-child(1) {
						width: 60%;
						border-right: 1px solid var(--color-dark-blue-white90);
					}

					.print-content__form-answers-table td:nth-child(2) {
						font-weight: var(--font-weight-semi-bold);
					}

					.print-content__signature {
						margin-top: 48px;
					}

					.page-break-after {
						page-break-after: always;
					}
				</style>
			</head>
			<body>
				<div class="print-content">
					<div class="print-content__header">
						<h1>Beregning af din lærlingeløn</h1>
						<p>${formatDate(new Date(), '22. feb 2022')}</p>
					</div>
					<div class="print-content__salary_div">
						<h2>Timeløn undervejs i din uddannelse.</h2>
						<div>Timelønnen er fastlagt i overenskomsten, som gælder frem til 01/03 2028.</div>

						<table class="print-content__salary__table">
							<thead>
								<tr>
									<th>Periode</th>
									<th>Antal dage</th>
									<th>Timeløn</th>
								</tr>
							</thead>
							<tbody>
								${result.map((period) => `
									<tr>
										<td>${formatDate(period.period.start, '22/2 2022')} - ${formatDate(period.period.end, '22/2 2022')}</td>
										<td>${period.days} dage</td>
										<td><span class="print-content__salary__value">${period.salary}</span> kr/time</td>
									</tr>
								`).join('')}
							</tbody>
						</table>

						<div>
							<p class="print-content__info-text">
								Får du eller har din løn været mindre end listen ovenfor, så få hjælp i din lokalafdeling:
								<a href="https://danskmetal.dk/løntjek">danskmetal.dk/løntjek</a>.
							</p>
						</div>
						<div class="page-break-after"></div>
					</div>
					<div class="print-content__form-answers-table">
						<h2>Beregningen er lavet ud fra følgende oplysninger</h2>
						<table>
							<thead>
								<tr>
									<th>Spørgsmål</th>
									<th>Dine svar</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>Hvordan er du ansat?</td>
									<td>${formData.employmentType === 'private' ? 'Privatansat' : 'Offentligt ansat'}</td>
								</tr>
								<tr>
									<td>Hvilken uddannelse er du i gang med?</td>
									<td>${formData.education}</td>
								</tr>
								<tr>
									<td>Hvornår er du udlært?</td>
									<td>${formatDate(formData.endDate, '22/2 2022')}</td>
								</tr>
								<tr>
									<td>Har du gennemført grundforløbet på din uddannelse, før du startede i praktik/lære?</td>
									<td>${formData.isBasicEducation === 'true' ? 'Ja' : 'Nej'}</td>
								</tr>
								<tr>
									<td>Er du voksenlærling?</td>
									<td>${formData.isAdultApprentice === 'true' ? 'Ja' : 'Nej'}</td>
								</tr>
							</tbody>
						</table>

						<div class="print-content__signature">
							<p><span style="font-weight: var(--font-weight-semi-bold);">Med venlig hilsen</span><br>Dansk Metal</p>
						</div>
					</div>
				</div>
			</body>
		</html>
	`;
}
